import React, { useState } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Accordion,
    AccordionSummary,
    AccordionDetails,
} from '@mui/material';
import {
    ExpandMore,
    Warning,
} from '@mui/icons-material';
import { RiskReportTypes } from '../../types';
import {categorizeRiskFactors, isSupplementalRiskFactor} from "../../helpers/crimeScore";

interface Props {
    assessmentData: RiskReportTypes.RiskResponse;
    isDownloadView ?: boolean
}

// Determine severity level for color coding
const getSeverityStyle = (factor: string) => {
    const lowerFactor = factor.toLowerCase();

    // Critical/High severity - Red
    if (lowerFactor.includes('guilty disposition') ||
        lowerFactor.includes('felony') ||
        lowerFactor.includes('serious charge') ||
        lowerFactor.includes('weapon') ||
        lowerFactor.includes('drug') ||
        lowerFactor.includes('sex offender at exact location') ||
        lowerFactor.includes('sex offender name')) {
        return {
            bgcolor: '#fee',
            color: '#c62828',
            borderColor: '#ffcdd2'
        };
    }

    // Medium severity - Yellow
    if (lowerFactor.includes('misdemeanor') ||
        lowerFactor.includes('crime rate') ||
        lowerFactor.includes('multiple') ||
        lowerFactor.includes('exact dob')) {
        return {
            bgcolor: '#fffbea',
            color: '#f57c00',
            borderColor: '#fff3cd'
        };
    }

    // Default - Light gray
    return {
        bgcolor: '#fffbea',
        color: '#f57c00',
        borderColor: '#fff3cd'
    };
};

export const RiskFactorsAccordionSection: React.FC<Props> = ({ assessmentData, isDownloadView }) => {
    // Sparse reports reach this panel now that the history table renders them
    // — see news-articles.tsx.
    const { riskAssessmentInfo } = assessmentData ?? {};
    const { riskFactors = [], findings } = riskAssessmentInfo ?? {};

    // Remove duplicates
    const uniqueRiskFactors = Array.from(new Set(riskFactors));
    const categorized = categorizeRiskFactors(uniqueRiskFactors);
    // News-derived factors are shown in the Related News section instead.
    const countedFactors = uniqueRiskFactors.filter((factor) => !isSupplementalRiskFactor(factor));

    // The criminal-history risk factors are generic summary strings ("Felony
    // guilty disposition on record"). Pull the actual charges out of the same
    // public-records data the Criminal Records tile uses so we can name the
    // charge (with its disposition) instead of just saying "guilty".
    const criminalCharges = Object.values(findings?.publicRecords ?? {})
        .flat()
        .flatMap((record: any) =>
            (record?.charges ?? []).map((charge: any) => ({
                description: charge?.description || 'Charge description unavailable',
                disposition: record?.disposition,
                court: record?.court,
            })),
        );

    // Count critical items (red items)
    const criticalCount = countedFactors.filter(factor => {
        const lowerFactor = factor.toLowerCase();
        return lowerFactor.includes('guilty disposition') ||
            lowerFactor.includes('felony') ||
            lowerFactor.includes('serious charge') ||
            lowerFactor.includes('weapon') ||
            lowerFactor.includes('drug') ||
            lowerFactor.includes('sex offender name') ||
            lowerFactor.includes('sex offender at exact location');
    }).length;

    const [expandedPanels, setExpandedPanels] = useState<string[]>([]);

    const handleAccordionChange = (panel: string) => {
        setExpandedPanels(prev =>
            prev.includes(panel)
                ? prev.filter(p => p !== panel)
                : [...prev, panel]
        );
    };

    if (countedFactors.length === 0) {
        return null;
    }

    return (
        <Card sx={{ mb: 3 }}>
            <CardContent sx={{ p: 4 }}>
                {/* Header with counts */}
                <Box className={'pdf-keep-with-next'} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Box>
                        <Typography variant="h6" fontWeight={600}>
                            Key Risk Factors
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Why this assessment was flagged
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                        <Typography variant="body2" sx={{ color: '#d32f2f', fontWeight: 600 }}>
                            Critical: {criticalCount}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            |
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Total: {countedFactors.length}
                        </Typography>
                    </Box>
                </Box>

                {/* Criminal History Accordion */}
                {categorized.criminalHistory.length > 0 && (
                    <Accordion
                        expanded={isDownloadView || expandedPanels.includes('criminal')}
                        onChange={() => handleAccordionChange('criminal')}
                        sx={{
                            mb: 2,
                            border: expandedPanels.includes('criminal') ? '2px solid #206FA5' : '1px solid #e0e0e0',
                            borderRadius: '8px !important',
                            '&:before': { display: 'none' },
                            boxShadow: 'none',
                        }}
                    >
                        <AccordionSummary
                            expandIcon={<ExpandMore />}
                            sx={{
                                '& .MuiAccordionSummary-content': {
                                    alignItems: 'center',
                                    gap: 1,
                                }
                            }}
                        >
                            <Warning sx={{ color: '#d32f2f', fontSize: 20 }} />
                            <Typography variant="body1" fontWeight={500}>
                                Criminal History
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                                ({criminalCharges.length > 0 ? criminalCharges.length : categorized.criminalHistory.length})
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={{ pt: 0 }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                {/* Prefer the actual charges (charge + disposition) over the
                                    generic "guilty disposition" factor strings. Fall back to
                                    the factor strings for sparse reports with no charge data. */}
                                {criminalCharges.length > 0
                                    ? criminalCharges.map((charge, index) => {
                                        const style = getSeverityStyle(`${charge.description} ${charge.disposition ?? ''}`);
                                        return (
                                            <Box
                                                key={index}
                                                className={'pdf-avoid-break'}
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1,
                                                    p: 1.5,
                                                    borderRadius: 1,
                                                    ...style,
                                                    border: `1px solid ${style.borderColor}`,
                                                }}
                                            >
                                                <Warning sx={{ fontSize: 18, color: style.color, flexShrink: 0 }} />
                                                <Typography variant="body2" sx={{ color: style.color, fontWeight: 500 }}>
                                                    {charge.description}
                                                    {charge.disposition ? ` — ${charge.disposition}` : ''}
                                                </Typography>
                                            </Box>
                                        );
                                    })
                                    : categorized.criminalHistory.map((factor, index) => {
                                        const style = getSeverityStyle(factor);
                                        return (
                                            <Box
                                                key={index}
                                                className={'pdf-avoid-break'}
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1,
                                                    p: 1.5,
                                                    borderRadius: 1,
                                                    ...style,
                                                    border: `1px solid ${style.borderColor}`,
                                                }}
                                            >
                                                <Warning sx={{ fontSize: 18, color: style.color }} />
                                                <Typography variant="body2" sx={{ color: style.color, fontWeight: 500 }}>
                                                    {factor}
                                                </Typography>
                                            </Box>
                                        );
                                    })}
                            </Box>
                        </AccordionDetails>
                    </Accordion>
                )}

                {/* Sex Offender Registry Accordion */}
                {categorized.sexOffender.length > 0 && (
                    <Accordion
                        expanded={isDownloadView || expandedPanels.includes('sexOffender')}
                        onChange={() => handleAccordionChange('sexOffender')}
                        sx={{
                            mb: 2,
                            border: expandedPanels.includes('sexOffender') ? '2px solid #206FA5' : '1px solid #e0e0e0',
                            borderRadius: '8px !important',
                            '&:before': { display: 'none' },
                            boxShadow: 'none',
                        }}
                    >
                        <AccordionSummary
                            expandIcon={<ExpandMore />}
                            sx={{
                                '& .MuiAccordionSummary-content': {
                                    alignItems: 'center',
                                    gap: 1,
                                }
                            }}
                        >
                            <Warning sx={{ color: '#d32f2f', fontSize: 20 }} />
                            <Typography variant="body1" fontWeight={500}>
                                Sex Offender Registry
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                                ({categorized.sexOffender.length})
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={{ pt: 0 }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                {categorized.sexOffender.map((factor, index) => {
                                    const style = getSeverityStyle(factor);
                                    return (
                                        <Box
                                            key={index}
                                            className={'pdf-avoid-break'}
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1,
                                                p: 1.5,
                                                borderRadius: 1,
                                                ...style,
                                                border: `1px solid ${style.borderColor}`,
                                            }}
                                        >
                                            <Warning sx={{ fontSize: 18, color: style.color }} />
                                            <Typography variant="body2" sx={{ color: style.color, fontWeight: 500 }}>
                                                {factor}
                                            </Typography>
                                        </Box>
                                    );
                                })}
                            </Box>
                        </AccordionDetails>
                    </Accordion>
                )}

                {/* Location Risks Accordion */}
                {categorized.locationRisks.length > 0 && (
                    <Accordion
                        expanded={isDownloadView || expandedPanels.includes('location')}
                        onChange={() => handleAccordionChange('location')}
                        sx={{
                            border: expandedPanels.includes('location') ? '2px solid #206FA5' : '1px solid #e0e0e0',
                            borderRadius: '8px !important',
                            '&:before': { display: 'none' },
                            boxShadow: 'none',
                        }}
                    >
                        <AccordionSummary
                            expandIcon={<ExpandMore />}
                            sx={{
                                '& .MuiAccordionSummary-content': {
                                    alignItems: 'center',
                                    gap: 1,
                                }
                            }}
                        >
                            <Warning sx={{ color: '#f57c00', fontSize: 20 }} />
                            <Typography variant="body1" fontWeight={500}>
                                Location Risks
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                                ({categorized.locationRisks.length})
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={{ pt: 0 }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                {categorized.locationRisks.map((factor, index) => {
                                    const style = getSeverityStyle(factor);
                                    return (
                                        <Box
                                            key={index}
                                            className={'pdf-avoid-break'}
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1,
                                                p: 1.5,
                                                borderRadius: 1,
                                                ...style,
                                                border: `1px solid ${style.borderColor}`,
                                            }}
                                        >
                                            <Warning sx={{ fontSize: 18, color: style.color }} />
                                            <Typography variant="body2" sx={{ color: style.color, fontWeight: 500 }}>
                                                {factor}
                                            </Typography>
                                        </Box>
                                    );
                                })}
                            </Box>
                        </AccordionDetails>
                    </Accordion>
                )}
            </CardContent>
        </Card>
    );
};